// src/components/RecordingSection.tsx
import {
  Box,
  Stack,
  Chip,
  LinearProgress,
  Typography,
  Button,
} from "@mui/material";
import { PlayArrow, Pause, Stop, Upload, Refresh } from "@mui/icons-material";
import { CircularProgress } from "@mui/material";
import { formatTime } from "../../../utils/formatTime";

interface RecordingSectionProps {
  status: string;
  timeRemaining: number;
  maxLength: number;
  mediaBlobUrl: string | null;
  isUploading: boolean;
  onStart: () => void;
  onPause: () => void;
  onResume: () => void;
  onStop: () => void;
  onUpload: () => void;
  onReset: () => void;
}

export function RecordingSection({
  status,
  timeRemaining,
  maxLength,
  mediaBlobUrl,
  isUploading,
  onStart,
  onPause,
  onResume,
  onStop,
  onUpload,
  onReset,
}: RecordingSectionProps) {
  const progress = ((maxLength - timeRemaining) / maxLength) * 100;

  return (
    <>
      {/* Progress */}
      <Box sx={{ mb: 4 }}>
        <Stack direction="row" justifyContent="center" sx={{ mb: 2 }}>
          <Chip
            label={`Time Remaining: ${formatTime(timeRemaining)}`}
            color="secondary"
          />
        </Stack>
        <LinearProgress
          variant="determinate"
          value={progress}
          color="primary"
          sx={{ height: 8, borderRadius: 1 }}
        />
      </Box>

      {/* Controls */}
      <Stack direction="row" spacing={2} justifyContent="center" sx={{ mb: 4 }}>
        {status === "idle" && (
          <Button
            variant="contained"
            size="large"
            onClick={onStart}
            startIcon={<PlayArrow />}
            color="primary"
          >
            Start Recording
          </Button>
        )}

        {status === "recording" && (
          <>
            <Button
              variant="contained"
              size="large"
              onClick={onPause}
              startIcon={<Pause />}
              color="secondary"
            >
              Pause
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={onStop}
              startIcon={<Stop />}
              color="error"
            >
              Stop
            </Button>
          </>
        )}

        {status === "paused" && (
          <>
            <Button
              variant="contained"
              size="large"
              onClick={onResume}
              startIcon={<PlayArrow />}
              color="primary"
            >
              Resume
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={onStop}
              startIcon={<Stop />}
              color="error"
            >
              Stop
            </Button>
          </>
        )}
      </Stack>

      {/* Preview */}
      {mediaBlobUrl && (
        <>
          <Box sx={{ mb: 4, textAlign: "center" }}>
            <Typography variant="h6" gutterBottom color="text.primary">
              Preview Your Recording
            </Typography>
            <audio
              src={mediaBlobUrl}
              controls
              autoPlay
              loop
              style={{ width: "100%", maxWidth: 400 }}
            />
          </Box>

          <Stack direction="row" spacing={2} justifyContent="center">
            <Button
              variant="contained"
              size="large"
              onClick={onUpload}
              startIcon={
                isUploading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <Upload />
                )
              }
              disabled={isUploading}
              color="success"
            >
              {isUploading ? "Uploading..." : "Upload Recording"}
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={onReset}
              startIcon={<Refresh />}
              disabled={isUploading}
              color="error"
            >
              Reset
            </Button>
          </Stack>
        </>
      )}
    </>
  );
}
